import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Calendar, Plus, Sparkles, Facebook, Instagram, Linkedin, Twitter,
  RefreshCw, Send, Image, Clock, BarChart2, Trash2, ChevronLeft,
  ChevronRight, GripVertical, ExternalLink, Unplug, Link2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const platforms = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'twitter', name: 'X/Twitter', icon: Twitter, color: '#000000' },
];

// ==================== SOCIAL ACCOUNT CARD ====================
const SocialAccountCard = ({ platform, account, onConnect, onDisconnect }) => {
  const PlatformIcon = platform.icon;
  const connected = !!account;

  return (
    <Card className="bg-white border-gray-border" data-testid={`social-account-${platform.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${platform.color}15` }}
            >
              <PlatformIcon className="h-5 w-5" style={{ color: platform.color }} />
            </div>
            <div>
              <p className="font-medium text-text-primary">{platform.name}</p>
              {connected ? (
                <p className="text-xs text-gray-medium">@{account.username}</p>
              ) : (
                <Badge variant="secondary">Not Connected</Badge>
              )}
            </div>
          </div>
          {connected && (
            <Badge className="bg-emerald-brand text-white">Connected</Badge>
          )}
        </div>
        {connected && (
          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-border">
            <div>
              <p className="text-xs text-gray-medium">Followers</p>
              <p className="font-semibold text-text-primary">{account.follower_count?.toLocaleString() || '0'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-medium">Engagement</p>
              <p className="font-semibold text-text-primary">{account.engagement_rate || '0'}%</p>
            </div>
          </div>
        )}
        <Button
          variant={connected ? 'outline' : 'default'}
          size="sm"
          className={cn("w-full mt-3", !connected && "bg-emerald-brand hover:bg-emerald-hover")}
          onClick={() => connected ? onDisconnect(account) : onConnect(platform)}
          data-testid={`${platform.id}-connect-btn`}
        >
          {connected ? (
            <><Unplug className="h-3.5 w-3.5 mr-1.5" />Disconnect</>
          ) : (
            <><Link2 className="h-3.5 w-3.5 mr-1.5" />Connect</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// ==================== POST CARD ====================
const PostCard = ({ post, onDelete }) => {
  const statusColors = {
    published: 'bg-emerald-brand',
    scheduled: 'bg-blue-500',
    draft: 'bg-gray-500',
  };
  return (
    <Card className="bg-white border-gray-border" data-testid={`post-card-${post.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex gap-1">
            {post.platforms?.map((p, i) => {
              const platform = platforms.find(pl => pl.id === p);
              const Icon = platform?.icon || Facebook;
              return (
                <div key={i} className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-100">
                  <Icon className="h-3 w-3" style={{ color: platform?.color }} />
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Badge className={cn("text-white text-xs", statusColors[post.status])}>
              {post.status}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-gray-medium hover:text-red-500"
              onClick={() => onDelete(post.id)}
              data-testid={`delete-post-${post.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <p className="text-sm text-text-primary line-clamp-3 mb-2">{post.content}</p>
        <div className="flex items-center gap-2 text-xs text-gray-medium">
          <Clock className="h-3 w-3" />
          {post.scheduled_at ? new Date(post.scheduled_at).toLocaleString() : 'Not scheduled'}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== CALENDAR COMPONENT ====================
const ContentCalendar = ({ posts, onReschedule, onCreatePost }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedPost, setDraggedPost] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const getPostsForDay = (day) => {
    return posts.filter(p => {
      if (!p.scheduled_at) return false;
      const d = new Date(p.scheduled_at);
      return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
    });
  };

  const handleDragStart = (e, post) => {
    setDraggedPost(post);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, day) => {
    e.preventDefault();
    if (!draggedPost) return;
    const newDate = new Date(year, month, day, 12, 0, 0);
    onReschedule(draggedPost.id, newDate.toISOString());
    setDraggedPost(null);
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = new Date();

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  return (
    <div data-testid="content-calendar">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="calendar-prev">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h3 className="text-lg font-heading font-semibold text-text-primary">
          {monthNames[month]} {year}
        </h3>
        <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="calendar-next">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {dayNames.map(d => (
          <div key={d} className="text-center text-xs font-medium text-gray-medium py-2">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
        {calendarDays.map((day, idx) => {
          const dayPosts = day ? getPostsForDay(day) : [];
          const isToday = day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

          return (
            <div
              key={idx}
              className={cn(
                "bg-white min-h-[80px] sm:min-h-[100px] p-1 sm:p-2 transition-colors",
                !day && "bg-gray-50",
                isToday && "ring-2 ring-inset ring-emerald-brand/30"
              )}
              onDragOver={day ? handleDragOver : undefined}
              onDrop={day ? (e) => handleDrop(e, day) : undefined}
            >
              {day && (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <span className={cn(
                      "text-xs font-medium",
                      isToday ? "bg-emerald-brand text-white w-6 h-6 rounded-full flex items-center justify-center" : "text-gray-medium"
                    )}>
                      {day}
                    </span>
                    {dayPosts.length === 0 && (
                      <button
                        onClick={() => onCreatePost(new Date(year, month, day, 12, 0))}
                        className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-gray-300 hover:text-emerald-brand transition-opacity"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {dayPosts.slice(0, 2).map(post => (
                      <div
                        key={post.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, post)}
                        className={cn(
                          "text-[10px] sm:text-xs px-1.5 py-0.5 rounded cursor-grab active:cursor-grabbing truncate",
                          post.status === 'published' ? 'bg-emerald-brand/10 text-emerald-brand' :
                          post.status === 'scheduled' ? 'bg-blue-50 text-blue-600' :
                          'bg-gray-100 text-gray-600'
                        )}
                        title={post.content}
                      >
                        <span className="flex items-center gap-0.5">
                          <GripVertical className="h-2.5 w-2.5 flex-shrink-0 hidden sm:block" />
                          {post.content?.slice(0, 20)}...
                        </span>
                      </div>
                    ))}
                    {dayPosts.length > 2 && (
                      <span className="text-[10px] text-gray-medium">+{dayPosts.length - 2} more</span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================
export const SocialMediaPage = () => {
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [connectUsername, setConnectUsername] = useState('');
  const [connectPageName, setConnectPageName] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [newPost, setNewPost] = useState({ content: '', platforms: [], scheduled_at: '' });
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [linkedinStatus, setLinkedinStatus] = useState({ connected: false });

  const fetchData = useCallback(async () => {
    try {
      const [postsRes, accountsRes, analyticsRes, liRes] = await Promise.all([
        axios.get(`${API_URL}/api/social/posts`),
        axios.get(`${API_URL}/api/social/accounts`),
        axios.get(`${API_URL}/api/social/analytics`),
        axios.get(`${API_URL}/api/linkedin/status`).catch(() => ({ data: { connected: false } })),
      ]);
      setPosts(postsRes.data.posts || []);
      setAccounts(accountsRes.data.accounts || []);
      setAnalyticsData(analyticsRes.data);
      setLinkedinStatus(liRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConnect = async (platform) => {
    if (platform.id === 'linkedin') {
      // LinkedIn uses OAuth — redirect to LinkedIn auth
      try {
        const { data } = await axios.get(`${API_URL}/api/linkedin/auth/url`);
        window.location.href = data.url;
      } catch (err) {
        toast.error('Failed to start LinkedIn authorization');
      }
      return;
    }
    setConnectingPlatform(platform);
    setConnectUsername('');
    setConnectPageName('');
    setConnectDialogOpen(true);
  };

  const handleLinkedinDisconnect = async () => {
    try {
      await axios.delete(`${API_URL}/api/linkedin/disconnect`);
      toast.success('LinkedIn disconnected');
      fetchData();
    } catch (err) {
      toast.error('Failed to disconnect LinkedIn');
    }
  };

  const handleDisconnect = async (account) => {
    try {
      await axios.delete(`${API_URL}/api/social/accounts/${account.id}`);
      toast.success(`${account.platform} disconnected`);
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect account');
    }
  };

  const handleConnectSubmit = async () => {
    if (!connectUsername.trim()) return;
    setConnecting(true);
    try {
      await axios.post(`${API_URL}/api/social/accounts/connect`, {
        platform: connectingPlatform.id,
        username: connectUsername.trim(),
        page_name: connectPageName.trim() || connectUsername.trim()
      });
      toast.success(`${connectingPlatform.name} connected successfully!`);
      setConnectDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect account');
    } finally {
      setConnecting(false);
    }
  };

  const handleCreatePost = async () => {
    try {
      // Save to internal social posts
      await axios.post(`${API_URL}/api/social/posts`, {
        ...newPost,
        scheduled_at: newPost.scheduled_at || null
      });

      // Also post to LinkedIn if selected and connected
      if (newPost.platforms.includes('linkedin') && linkedinStatus.connected && !newPost.scheduled_at) {
        try {
          await axios.post(`${API_URL}/api/linkedin/posts`, {
            text: newPost.content
          });
          toast.success('Posted to LinkedIn!');
        } catch (liErr) {
          toast.error('Post saved locally but LinkedIn posting failed');
        }
      }

      toast.success('Post created!');
      setCreateDialogOpen(false);
      setNewPost({ content: '', platforms: [], scheduled_at: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API_URL}/api/social/posts/${postId}`);
      toast.success('Post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleReschedule = async (postId, newDate) => {
    try {
      await axios.put(`${API_URL}/api/social/posts/${postId}`, {
        scheduled_at: newDate
      });
      toast.success('Post rescheduled');
      fetchData();
    } catch (error) {
      toast.error('Failed to reschedule post');
    }
  };

  const handleGenerateCaption = async () => {
    if (!aiTopic) return;
    setGeneratingCaption(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-social-captions`, {
        platforms: newPost.platforms.length > 0 ? newPost.platforms : ['facebook'],
        topic: aiTopic,
        hashtags: true
      });
      setNewPost(prev => ({ ...prev, content: res.data.captions }));
      toast.success('Caption generated!');
    } catch (error) {
      toast.error('AI generation failed');
    } finally {
      setGeneratingCaption(false);
    }
  };

  const togglePlatform = (platformId) => {
    setNewPost(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const openCreateForDate = (date) => {
    setNewPost({
      content: '',
      platforms: [],
      scheduled_at: date.toISOString().slice(0, 16)
    });
    setCreateDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="social-media-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Social Media<PageInfoTooltip pageKey="social-media" /></h1>
          <div className="flex items-center gap-2">
            <p className="text-gray-medium">Manage and automate your social presence</p>
            {analyticsData && (
              <Badge
                className={cn("text-[10px]",
                  analyticsData.data_source === 'live'
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700"
                )}
                data-testid="social-data-source"
              >
                {analyticsData.data_source === 'live' ? 'Live Data' : 'Demo Data'}
              </Badge>
            )}
          </div>
        </div>
        <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={() => setCreateDialogOpen(true)} data-testid="create-post-btn">
          <Plus className="h-4 w-4" />
          Create Post
        </Button>
      </div>

      {/* Create Post Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Create New Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="mb-2 block">Select Platforms</Label>
              <div className="flex flex-wrap gap-3">
                {platforms.map((platform) => (
                  <div
                    key={platform.id}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all",
                      newPost.platforms.includes(platform.id)
                        ? "border-emerald-brand bg-emerald-brand/5"
                        : "border-gray-border hover:border-gray-300"
                    )}
                    onClick={() => togglePlatform(platform.id)}
                  >
                    <Checkbox checked={newPost.platforms.includes(platform.id)} />
                    <platform.icon className="h-4 w-4" style={{ color: platform.color }} />
                    <span className="text-sm">{platform.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <Card className="bg-emerald-brand/5 border-emerald-brand/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-5 w-5 text-emerald-brand" />
                  <span className="font-medium text-text-primary">Generate with AI</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter topic or keyword..."
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                    className="flex-1"
                    data-testid="ai-topic-input"
                  />
                  <Button
                    onClick={handleGenerateCaption}
                    disabled={generatingCaption || !aiTopic}
                    className="bg-emerald-brand hover:bg-emerald-hover"
                    data-testid="generate-caption-btn"
                  >
                    {generatingCaption ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div>
              <Label htmlFor="content">Caption</Label>
              <Textarea
                id="content"
                placeholder="Write your caption..."
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                className="min-h-[120px] mt-2"
                data-testid="post-content-input"
              />
              <p className="text-xs text-gray-medium mt-1">{newPost.content.length} characters</p>
            </div>

            <div>
              <Label htmlFor="schedule">Schedule (optional)</Label>
              <Input
                id="schedule"
                type="datetime-local"
                value={newPost.scheduled_at}
                onChange={(e) => setNewPost(prev => ({ ...prev, scheduled_at: e.target.value }))}
                className="mt-2"
                data-testid="schedule-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setCreateDialogOpen(false)}>
                Save Draft
              </Button>
              <Button className="flex-1 bg-emerald-brand hover:bg-emerald-hover" onClick={handleCreatePost} data-testid="submit-post-btn">
                <Send className="h-4 w-4 mr-2" />
                {newPost.scheduled_at ? 'Schedule' : 'Post Now'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Connect Account Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {connectingPlatform && (
                <>
                  <connectingPlatform.icon className="h-5 w-5" style={{ color: connectingPlatform.color }} />
                  Connect {connectingPlatform.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                Enter your {connectingPlatform?.name} profile details to connect your account.
              </p>
            </div>
            <div>
              <Label htmlFor="oauth-username">Username / Handle</Label>
              <Input
                id="oauth-username"
                placeholder={`@your${connectingPlatform?.id || ''}handle`}
                value={connectUsername}
                onChange={(e) => setConnectUsername(e.target.value)}
                className="mt-2"
                data-testid="connect-username-input"
              />
            </div>
            <div>
              <Label htmlFor="oauth-page">Page / Business Name (optional)</Label>
              <Input
                id="oauth-page"
                placeholder="My Business Page"
                value={connectPageName}
                onChange={(e) => setConnectPageName(e.target.value)}
                className="mt-2"
                data-testid="connect-pagename-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
            <Button
              className="bg-emerald-brand hover:bg-emerald-hover"
              onClick={handleConnectSubmit}
              disabled={connecting || !connectUsername.trim()}
              data-testid="connect-submit-btn"
            >
              {connecting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Authorize & Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm whitespace-nowrap">Overview</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar" className="text-xs sm:text-sm whitespace-nowrap">Calendar</TabsTrigger>
          <TabsTrigger value="posts" data-testid="tab-posts" className="text-xs sm:text-sm whitespace-nowrap">Posts</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-xs sm:text-sm whitespace-nowrap">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* LinkedIn OAuth Connection Banner */}
          {linkedinStatus.connected ? (
            <Card className="bg-blue-50 border-blue-200" data-testid="linkedin-connected-banner">
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Linkedin className="h-5 w-5 text-[#0A66C2]" />
                  <div>
                    <p className="text-xs font-medium text-blue-800">LinkedIn Connected via OAuth</p>
                    <p className="text-[10px] text-blue-600">Posts will be published directly to your Company Page</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="text-xs text-red-500 border-red-200" onClick={handleLinkedinDisconnect} data-testid="linkedin-disconnect-btn">
                  <Unplug className="h-3.5 w-3.5 mr-1" /> Disconnect
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gradient-to-r from-[#0A66C2]/5 to-blue-50 border-blue-200" data-testid="linkedin-connect-banner">
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Linkedin className="h-5 w-5 text-[#0A66C2]" />
                  <div>
                    <p className="text-xs font-medium text-blue-800">Connect LinkedIn with OAuth</p>
                    <p className="text-[10px] text-blue-600">Authorize to post directly to your Company Page and see live analytics</p>
                  </div>
                </div>
                <Button size="sm" className="bg-[#0A66C2] hover:bg-[#094c8f] text-xs" onClick={() => handleConnect({ id: 'linkedin' })} data-testid="linkedin-oauth-btn">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Authorize LinkedIn
                </Button>
              </CardContent>
            </Card>
          )}

          <div>
            <h2 className="text-lg font-heading font-semibold text-text-primary mb-4">Connected Accounts</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {platforms.map((platform) => {
                const account = accounts.find(a => a.platform === platform.id);
                return (
                  <SocialAccountCard
                    key={platform.id}
                    platform={platform}
                    account={account}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                  />
                );
              })}
            </div>
          </div>

          <Card className="bg-white border-gray-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-brand" />
                AI Autopilot
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-medium mb-4">
                Enable autopilot to let AI automatically create and schedule posts based on your brand voice.
              </p>
              <div className="flex items-center justify-between p-3 bg-off-white rounded-lg">
                <span className="font-medium text-text-primary">Enable Autopilot</span>
                <Switch data-testid="autopilot-switch" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar">
          <Card className="bg-white border-gray-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Calendar className="h-5 w-5 text-emerald-brand" />
                Content Calendar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ContentCalendar
                posts={posts}
                onReschedule={handleReschedule}
                onCreatePost={openCreateForDate}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="posts">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.length > 0 ? (
              posts.map((post) => <PostCard key={post.id} post={post} onDelete={handleDeletePost} />)
            ) : (
              <Card className="col-span-full bg-white border-gray-border">
                <CardContent className="p-8 text-center">
                  <Image className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-medium">No posts yet</p>
                  <p className="text-sm text-gray-medium mb-4">Create your first post to get started</p>
                  <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Post
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          {/* Summary Stats */}
          {analyticsData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-white border-gray-border">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-text-primary">{analyticsData.total_followers?.toLocaleString()}</p>
                  <p className="text-xs text-gray-medium">Total Followers</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-text-primary">{analyticsData.avg_engagement_rate}%</p>
                  <p className="text-xs text-gray-medium">Avg Engagement</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-text-primary">{analyticsData.published_posts}</p>
                  <p className="text-xs text-gray-medium">Published Posts</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-text-primary">{analyticsData.scheduled_posts}</p>
                  <p className="text-xs text-gray-medium">Scheduled</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Platform Performance */}
          <Card className="bg-white border-gray-border" data-testid="analytics-platform-perf">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <BarChart2 className="h-5 w-5 text-emerald-brand" />
                Platform Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {accounts.length > 0 ? (
                <div className="space-y-4">
                  {accounts.map((acc) => {
                    const plat = platforms.find(p => p.id === acc.platform);
                    const Icon = plat?.icon || Facebook;
                    const stats = analyticsData?.platform_stats?.[acc.platform] || {};
                    return (
                      <div key={acc.id} className="p-4 bg-off-white rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Icon className="h-5 w-5" style={{ color: plat?.color }} />
                            <div>
                              <p className="font-medium text-text-primary">@{acc.username}</p>
                              <p className="text-xs text-gray-medium">{plat?.name}</p>
                            </div>
                          </div>
                          <Badge className="bg-emerald-brand/10 text-emerald-brand">{acc.engagement_rate}% eng.</Badge>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div>
                            <p className="text-xs text-gray-medium">Followers</p>
                            <p className="text-sm font-semibold text-text-primary">{acc.follower_count?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-medium">Reach</p>
                            <p className="text-sm font-semibold text-text-primary">{(stats.total_reach || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-medium">Likes</p>
                            <p className="text-sm font-semibold text-text-primary">{(stats.total_likes || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-medium">Shares</p>
                            <p className="text-sm font-semibold text-text-primary">{(stats.total_shares || 0).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-medium">
                  <div className="text-center">
                    <BarChart2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Connect accounts to see analytics</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Best Posting Times & Weekly Trend */}
          {analyticsData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-white border-gray-border" data-testid="best-times">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    Best Posting Times
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(analyticsData.best_times || []).map((t, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-off-white">
                        <div>
                          <p className="text-sm font-medium text-text-primary">{t.day}</p>
                          <p className="text-xs text-gray-medium">{t.hour}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-brand rounded-full" style={{ width: `${t.engagement_score}%` }} />
                          </div>
                          <span className="text-xs font-medium text-emerald-brand">{t.engagement_score}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-border" data-testid="weekly-engagement">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    <BarChart2 className="h-4 w-4 text-blue-500" />
                    Weekly Engagement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(analyticsData.weekly_trend || []).map((w, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-gray-medium w-14 flex-shrink-0">{w.week}</span>
                        <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden relative">
                          <div className="h-full bg-blue-400/70 rounded-full" style={{ width: `${Math.min((w.engagement / 2000) * 100, 100)}%` }} />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium text-text-primary">
                            {w.engagement} | +{w.followers_gained}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SocialMediaPage;
